import { useApi } from "@lib/helpers/useApi";
import { FetchServiceAxios } from "@lib/helpers/fetchServiceAxios";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { getFormatedDateForOrders } from "@lib/utils/order-history/helpers";
import { useRouter } from "next/router";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Grid } from "@ui/components/layout/grid/Grid";
import { Skeleton } from "@ui/components/data-display/skeleton/Skeleton";
import { NextLink } from "@ui/components/navigation/link/NextLink";
import { VStack } from "@ui/components/layout/stack/Stack";
import { Heading } from "@ui/components/content/heading/Heading";
import { Text } from "@ui/components/content/text/Text";
import StarsRating from "@ui/components/feedback/stars-rating/StarsRating";
import { Flex } from "@ui/components/layout/flex/Flex";
import { NextImage } from "@ui/components/media-and-icons/image/NextImage";
import { Carousel } from "@ui/components/navigation/carousel/Carousel";
import { Box } from "@ui/components/layout/box/Box";
import { activeSiteConfig } from "@config/site/site-config";
import type { TrustpilotReview } from "./types";

function getTrustpilotPageUrl(locale: string) {
    const { country, language } = activeSiteConfig;
    const normalizedCountry = country.toLowerCase();

    //Next few lines of code should be removed when we'll find better approach
    const listOfLocalesNotSupportedByTrustpilot = ["hr"];

    if (listOfLocalesNotSupportedByTrustpilot.includes(normalizedCountry)) {
        return `https://www.trustpilot.com/review/ecco.com`;
    }

    if (normalizedCountry === "gb") {
        return `https://uk.trustpilot.com/review/ecco.com`;
    }

    const prefix = language.length > 1 ? locale.toLowerCase() : normalizedCountry;
    return `https://${prefix}.trustpilot.com/review/ecco.com`;
}

const TrustpilotWidget = () => {
    const { locale = "en" } = useRouter();

    const { get } = useMicrocopy();

    const [language] = locale.split("-");

    const {
        data: trustpilotData = {
            totalReviews: 0,
            starsScore: 0,
            reviews: [],
        },
        isLoading,
    } = useApi.useAsync<{ totalReviews: number; starsScore: number; reviews: TrustpilotReview[] }>(
        [QueryKeys.getTrustpilotReviews],
        async () => {
            return await FetchServiceAxios.get("/api/trustpilot/reviews", { params: { language } });
        }
    );

    const defaultCarouselWidth = "calc(100vw - 32px)";

    return (
        <Grid
            p={4}
            gridTemplateColumns={[
                defaultCarouselWidth,
                defaultCarouselWidth,
                defaultCarouselWidth,
                "300px calc(100vw - 400px)",
            ]}
        >
            {isLoading ? (
                <Skeleton h={"200px"} />
            ) : (
                <NextLink
                    href={getTrustpilotPageUrl(locale)}
                    target="_blank"
                    _hover={{ textDecoration: "none" }}
                >
                    <VStack align="flex-start" mb={6} spacing={1} textDecoration="none">
                        <Heading size="h3" fontWeight="bold" mb={2}>
                            {get(CommonCMS.shared, CommonCMS.reviewsWidget.excellent)}
                        </Heading>
                        <StarsRating score={trustpilotData.starsScore} size="md" />
                        <Text fontSize="md" mt={1}>
                            {get(CommonCMS.shared, CommonCMS.reviewsWidget.totalReviews, {
                                replacements: { totalReviews: trustpilotData.totalReviews },
                            })}
                        </Text>
                        <Text fontSize="md" color="gray.600">
                            {get(CommonCMS.shared, CommonCMS.reviewsWidget.disclaimer, {
                                replacements: { min: 3, max: 5 },
                            })}
                        </Text>
                        <Flex align="center" mt={2}>
                            <NextImage
                                alt="Trustpilot reviews"
                                width={150}
                                src="https://cdn.trustpilot.net/brand-assets/4.3.0/logo-black.svg"
                            />
                        </Flex>
                    </VStack>
                </NextLink>
            )}
            <Carousel
                showNavigation={false}
                slidesPerView={[1.5, 3, 3, 5]}
                gap={4}
                px={0}
                py={3}
                w={"100%"}
            >
                {isLoading ? (
                    <Skeleton h={"205px"} />
                ) : (
                    trustpilotData?.reviews?.map((review) => (
                        <NextLink
                            href={
                                "https://www.trustpilot.com/reviews/" +
                                review.links[0].href.split("/").reverse()[0]
                            }
                            key={review.id}
                            target="_blank"
                            w="100%"
                        >
                            <Box p={2} color={"black"} w="100%">
                                <VStack align="flex-start" spacing={2}>
                                    <Flex justify="space-between" w="100%" gap={1}>
                                        <StarsRating score={review.stars} size="xs" />
                                        <Text
                                            fontSize="xs"
                                            color="gray.500"
                                            textOverflow={"ellipsis"}
                                            whiteSpace={"nowrap"}
                                        >
                                            {getFormatedDateForOrders(review.createdAt)}
                                        </Text>
                                    </Flex>
                                    <Heading size="sm" fontWeight="bold" w="100%">
                                        {review.title}
                                    </Heading>
                                    <Text fontSize="sm" noOfLines={1} w="100%">
                                        {review.text}
                                    </Text>
                                    <Text fontWeight="bold">{review.consumer.displayName}</Text>
                                </VStack>
                            </Box>
                        </NextLink>
                    ))
                )}
            </Carousel>
        </Grid>
    );
};

export default TrustpilotWidget;
